import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Award, Target, Users, ArrowRight } from 'lucide-react';

const TexasAM: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Texas A&M";

    return (
        <div className="min-h-screen bg-white font-sans text-[#500000] selection:bg-[#500000] selection:text-white">
            {/* Utility Bar */}
            <div className="bg-[#333] text-gray-300 py-1 px-6 flex justify-end gap-6 text-[10px] font-bold uppercase tracking-widest">
                <a href="#" className="hover:text-white">Howdy!</a>
                <a href="#" className="hover:text-white">Directory</a>
                <a href="#" className="hover:text-white">Map</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="bg-[#500000] text-white font-serif font-black text-2xl w-10 h-10 flex items-center justify-center rounded-sm">AM</div>
                         <div className="flex flex-col">
                            <span className="font-serif text-xl font-bold text-[#500000] uppercase tracking-wide leading-none">{schoolName}</span>
                            <span className="font-serif text-xs font-bold text-[#500000] uppercase tracking-wider leading-none">University</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#500000] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#333] transition-colors border-b-2 border-transparent hover:border-[#500000] pb-1">About</a>
                        <a href="#" className="hover:text-[#333] transition-colors border-b-2 border-transparent hover:border-[#500000] pb-1">Admissions</a>
                        <a href="#" className="hover:text-[#333] transition-colors border-b-2 border-transparent hover:border-[#500000] pb-1">Academics</a>
                        <a href="#" className="hover:text-[#333] transition-colors border-b-2 border-transparent hover:border-[#500000] pb-1">Research</a>
                        <a href="#" className="hover:text-[#333] transition-colors border-b-2 border-transparent hover:border-[#500000] pb-1">Athletics</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Menu className="cursor-pointer lg:hidden text-[#500000]" size={28} />
                         <Search className="cursor-pointer text-[#500000]" size={22} />
                    </div>
                </div>
            </nav>

            {/* Hero - Corp of Cadets / Tradition */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1549419168-3e33f374773c?q=80&w=2070&auto=format&fit=crop" 
                    alt="Kyle Field" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#500000]/90 via-[#500000]/40 to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-2xl text-white">
                         <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6">
                             Fearless on Every Front.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-medium text-gray-200">
                             We are dedicated to the discovery, development, communication, and application of knowledge.
                         </p>
                         <Button className="bg-white text-[#500000] hover:bg-gray-100 font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-lg">
                             Explore {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Core Values */}
            <section className="py-24 px-6 md:px-12 bg-[#f4f4f4]">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#333]">
                     <div>
                         <Award size={48} className="text-[#500000] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Excellence</h3>
                         <p className="text-gray-600">Setting the bar high in everything we do.</p>
                     </div>
                      <div>
                         <Target size={48} className="text-[#500000] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Leadership</h3>
                         <p className="text-gray-600">Preparing students to lead in their careers and communities.</p>
                     </div>
                      <div>
                         <Users size={48} className="text-[#500000] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Service</h3>
                         <p className="text-gray-600">Serving the state, nation, and world through selfless acts.</p>
                     </div>
                </div>
            </section>

             {/* News Aggie Spirit */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <div className="flex justify-between items-center mb-12">
                         <h2 className="text-3xl font-serif font-bold text-[#500000] uppercase tracking-wide">{schoolName} Today</h2>
                         <Button variant="outline" className="text-[#500000] border-[#500000] hover:bg-[#500000] hover:text-white font-bold uppercase text-xs tracking-widest">Read All</Button>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "New agricultural engineering building opens for fall semester",
                             "Research team receives grant for renewable energy project",
                             "Bush School hosts international diplomacy summit",
                             "Aggie football announces new head coach"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer border-b-4 border-transparent hover:border-[#500000] pb-4 transition-colors">
                                 <div className="h-40 bg-gray-200 mb-4 overflow-hidden relative">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+60}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="News"/>
                                 </div>
                                 <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest mb-2 block">Campus News</span>
                                 <h3 className="text-lg font-bold text-[#333] group-hover:text-[#500000] transition-colors mb-2 leading-tight">{title}</h3>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#111] text-gray-500 py-16 px-6 md:px-12 border-t-8 border-[#500000]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-black text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="mb-6">
                              College Station, TX 77843 <br/>
                              (979) 845-3211
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest text-[#500000]">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Site Policies</a></li>
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Security</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Legislative Request</a></li>
                              <li><a href="#">Open Records</a></li>
                              <li><a href="#">Contact</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-800 pt-8">
                      © 2026 {schoolName} University
                  </div>
             </footer>
        </div>
    );
};

export default TexasAM;
